import React, { useState } from 'react';
import { Box, Button, TextField, Stack, IconButton, Tooltip, Typography } from '@mui/material';
import { SearchOutlined, ClearOutlined, PrecisionManufacturingOutlined } from '@mui/icons-material';
import { usePMDetailsEditor } from '../../../../hooks/usePMDetailsEditor';
import { AssetPickerDialog } from '../../../shared/AssetPickerDialog';
import { AssetListView } from '../../../../../../services/AssetService/types/assetTypes';
import { EditorMode } from '../../../../types/editorTypes';
import { AssetDocumentsPanel } from './AssetDocumentsPanel';

interface Props {
  mode: EditorMode;
}

interface AssetInfo {
  assetId?: string;
  assetTag?: string;
  assetName?: string;
}

interface AssetPickerButtonProps {
  centerId: string;
  hasAsset: boolean;
  onClick: () => void;
}

const AssetPickerButton: React.FC<AssetPickerButtonProps> = ({ centerId, hasAsset, onClick }) => (
  <Box>
    <Tooltip title={!centerId ? 'Select a center first' : ''}>
      <span>
        <Button
          variant="outlined"
          size="small"
          startIcon={hasAsset ? <PrecisionManufacturingOutlined /> : <SearchOutlined />}
          onClick={onClick}
          disabled={!centerId}
          sx={{ height: 40, whiteSpace: 'nowrap', fontWeight: 600, borderColor: '#e2e8f0', color: '#1e293b', '&:hover': { borderColor: '#3b82f6', backgroundColor: '#f0f7ff' } }}
        >
          {hasAsset ? 'Change Asset' : 'Select Asset'}
        </Button>
      </span>
    </Tooltip>
    {!centerId && (
      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#ef4444' }}>
        Select a center first
      </Typography>
    )}
  </Box>
);

interface AssetDetailFieldsProps {
  assetInfo: AssetInfo;
  onClear: () => void;
}

const AssetDetailFields: React.FC<AssetDetailFieldsProps> = ({ assetInfo, onClear }) => (
  <>
    <Box sx={{ flex: 1 }}>
      <TextField size="small" fullWidth label="Asset ID" value={assetInfo.assetId || ''} disabled slotProps={{ input: { readOnly: true } }} />
    </Box>
    <Box sx={{ flex: 1 }}>
      <TextField size="small" fullWidth label="Asset Tag" value={assetInfo.assetTag || ''} disabled slotProps={{ input: { readOnly: true } }} />
    </Box>
    <Box sx={{ flex: 1 }}>
      <TextField size="small" fullWidth label="Asset Name" value={assetInfo.assetName || ''} disabled slotProps={{ input: { readOnly: true } }} />
    </Box>
    <Tooltip title="Clear asset selection">
      <IconButton onClick={onClear} size="small" sx={{ mt: 0.5 }}>
        <ClearOutlined sx={{ fontSize: 18, color: '#64748b' }} />
      </IconButton>
    </Tooltip>
  </>
);

export const AssetLookupEditor: React.FC<Props> = ({ mode }) => {
  const { pmDetails, assetInfo, updateAsset } = usePMDetailsEditor(mode);
  const [dialogOpen, setDialogOpen] = useState(false);

  const centerId = pmDetails?.centerId || '';
  const hasAsset = !!assetInfo.assetId;

  const handleSelect = (asset: AssetListView) => {
    updateAsset({ assetId: asset.id, assetTag: asset.assetTag, assetName: asset.name });
  };

  const handleClear = () => {
    updateAsset({ assetId: '', assetTag: '', assetName: '' });
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <AssetPickerButton centerId={centerId} hasAsset={hasAsset} onClick={() => setDialogOpen(true)} />
        {hasAsset && <AssetDetailFields assetInfo={assetInfo} onClear={handleClear} />}
      </Stack>

      {mode === 'ticket' && hasAsset && (
        <Box sx={{ mt: 1.5 }}>
          <AssetDocumentsPanel assetId={assetInfo.assetId!} />
        </Box>
      )}

      <AssetPickerDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={handleSelect}
        centerId={centerId}
      />
    </Box>
  );
};

export default AssetLookupEditor;
